import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PlayCircleOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Select } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { useBatDauChay, useHoiTuyChonChay, useLuotGanNhat } from '../../hooks/useOpenCore';
import { KHOA_RPA } from '../../hooks/useRpa';
import { ketQuaLuot, maLuot } from '../../nghiep-vu/workflow';
import type { LuotGanNhat } from '../../types';

/**
 * Nút "Bắt đầu" (`WorkflowRunButton` của native): gửi lệnh chạy với giá trị mặc định đã lưu ở tab Biến & tham số
 * (`inputData: {}`); nút thành "Đang chạy" tới khi Robot chạy xong (lượt gần nhất hỏi lại mỗi 3 giây). Chỉ hỏi
 * Project khi Workflow được phép chạy ở nhiều Project.
 */
export function NutChay({ workflowId, ten }: { workflowId: string; ten: string }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const qc = useQueryClient();
  const hoiTuyChon = useHoiTuyChonChay();
  const batDau = useBatDauChay(workflowId);
  const ganNhat = useLuotGanNhat(workflowId, true);
  const [dangHoi, setDangHoi] = useState(false);
  const [theoDoi, setTheoDoi] = useState('');
  const [chonProject, setChonProject] = useState<{ id: string; name: string }[] | null>(null);

  /*
   * Báo một lần khi lượt mình vừa bấm kết thúc. Effect ở đây là đồng bộ với dữ liệu ngoài (kết quả hỏi lại của máy
   * chủ), không phải dẫn xuất state. Lỗi Robot thường là cả stack trace: chỉ báo lỗi, chi tiết ở Lịch sử luồng chạy.
   */
  const ketQua = ketQuaLuot(theoDoi, ganNhat.data);
  // StrictMode chạy effect hai lần ở bản dev: nhớ lượt đã báo để không báo trùng.
  const daBao = useRef('');
  useEffect(() => {
    if (!ketQua || daBao.current === theoDoi) return;
    daBao.current = theoDoi;
    setTheoDoi('');
    if (ketQua === 'xong') toast.success(t('rpaChay.xong', { ten }));
    else if (ketQua === 'matDau') toast.warning(t('rpaChay.matDau', { ten }));
    else toast.error(t('rpaChay.loi', { ten }));
  }, [ketQua, theoDoi, ten, t, toast]);

  /** `trongHop`: lỗi hiện trong hộp chọn Project (hộp không đóng), không bắn toast. */
  const gui = async (projectId: string, robotName: string, trongHop = false): Promise<boolean> => {
    try {
      const luot = await batDau.mutateAsync({ projectId });
      const ma = maLuot(luot);
      setTheoDoi(ma);
      // Đặt sẵn "đang chạy" để nút không nháy về "Bắt đầu" trong nhịp chờ lần hỏi đầu tiên.
      qc.setQueryData<LuotGanNhat>(KHOA_RPA.luotGanNhat(workflowId), {
        active: true,
        run: { id: ma, status: luot.status || 'queued', robotName: luot.robotName || robotName, errorMessage: '', requestedAt: null, finishedAt: null },
      });
      return true;
    } catch (e) {
      if (!trongHop) toast.error(dichLoi(e));
      return false;
    }
  };

  const bam = async () => {
    setDangHoi(true);
    try {
      const tc = await hoiTuyChon(workflowId);
      const [mot] = tc.projects;
      if (!mot) toast.error(t('rpaChay.khongCoProject'));
      else if (tc.projects.length === 1) await gui(mot.id, tc.robotName);
      else setChonProject(tc.projects);
    } catch (e) {
      toast.error(dichLoi(e));
    } finally {
      setDangHoi(false);
    }
  };

  const dangChay = ganNhat.data?.active ? ganNhat.data.run : null;

  return (
    <>
      {dangHoi || batDau.isPending || dangChay ? (
        <span
          role="status"
          title={dangChay?.robotName ? t('rpaChay.dangChayTren', { robot: dangChay.robotName }) : undefined}
          className="inline-flex h-7 items-center gap-2 rounded border border-success-500 bg-success-50 px-3 text-sm font-semibold text-success-700"
        >
          <span className="size-2 animate-pulse rounded-full bg-success-500 motion-reduce:animate-none" aria-hidden />
          {t('rpaChay.dangChay')}
        </span>
      ) : (
        <Button variant="primary" size="sm" icon={<PlayCircleOutlined />} onClick={() => void bam()}>
          {t('rpaChay.batDau')}
        </Button>
      )}
      {chonProject && (
        <HopChonProject
          ten={ten}
          projects={chonProject}
          dangGui={batDau.isPending}
          loi={batDau.error ? dichLoi(batDau.error) : null}
          onClose={() => {
            batDau.reset();
            setChonProject(null);
          }}
          onChay={async (projectId) => {
            if (await gui(projectId, '', true)) setChonProject(null);
          }}
        />
      )}
    </>
  );
}

function HopChonProject({
  ten,
  projects,
  dangGui,
  loi,
  onClose,
  onChay,
}: {
  ten: string;
  projects: { id: string; name: string }[];
  dangGui: boolean;
  loi: string | null;
  onClose: () => void;
  onChay: (projectId: string) => void;
}) {
  const { t } = useT();
  const [projectId, setProjectId] = useState('');
  return (
    <Dialog
      open
      width={480}
      title={t('rpaChay.chonProjectTieuDe', { ten })}
      closable={!dangGui}
      maskClosable={!dangGui}
      onClose={() => {
        if (!dangGui) onClose();
      }}
      footer={
        <div className="space-y-3">
          {loi && <LoiTrongHop>{loi}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" disabled={dangGui} onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" loading={dangGui} disabled={!projectId} onClick={() => onChay(projectId)}>
              {t('rpaChay.chay')}
            </Button>
          </div>
        </div>
      }
    >
      <p className="mb-3 text-sm text-content-secondary">{t('rpaChay.chonProjectMoTa')}</p>
      <Field label={t('rpaChay.project')} required>
        {(p) => (
          <Select<string>
            {...p}
            className="w-full"
            value={projectId || undefined}
            placeholder={t('rpaChay.chonProject')}
            disabled={dangGui}
            onChange={setProjectId}
            options={projects.map((pr) => ({ value: pr.id, label: pr.name }))}
          />
        )}
      </Field>
    </Dialog>
  );
}
